/**
 * TaskStreamingService
 * Service for streaming/chunked loading of tasks for large datasets
 */
import { TaskService } from './TaskService';
/**
 * TaskStreamingService
 * Handles chunked/streaming data loading for large task datasets
 * Uses client-side pagination strategy with AsyncGenerator pattern
 */
export class TaskStreamingService {
    constructor(http) {
        this.taskService = new TaskService(http);
    }
    /**
     * Stream tasks with pagination for user/admin views
     * @param filter - Filter criteria
     * @param chunkSize - Number of tasks per chunk (default: 50)
     * @yields Arrays of task model views as chunks
     *
     * @example
     * ```typescript
     * for await (const chunk of streamingService.streamTasks(filter, 50)) {
     *   console.log(`Loaded ${chunk.length} tasks`);
     *   // Process chunk
     * }
     * ```
     */
    async *streamTasks(filter, chunkSize = 50) {
        let page = 1;
        let hasMore = true;
        while (hasMore) {
            // Note: Backend doesn't support pagination yet
            // This is a placeholder for future pagination support
            // For now, we'll fetch all tasks and chunk them client-side
            const tasks = await this.taskService.listView(filter);
            if (tasks.length === 0) {
                hasMore = false;
                break;
            }
            // Client-side chunking
            for (let i = 0; i < tasks.length; i += chunkSize) {
                const chunk = tasks.slice(i, i + chunkSize);
                if (chunk.length > 0) {
                    yield chunk;
                }
            }
            // Since we're loading all at once, stop after first iteration
            hasMore = false;
        }
    }
    /**
     * Stream admin tasks with pagination for large datasets
     * @param filter - Admin filter criteria
     * @param chunkSize - Number of tasks per chunk (default: 100)
     * @yields Arrays of task model views as chunks
     *
     * @example
     * ```typescript
     * for await (const chunk of streamingService.streamAdminTasks(filter, 100)) {
     *   console.log(`Loaded ${chunk.length} admin tasks`);
     *   // Process chunk
     * }
     * ```
     */
    async *streamAdminTasks(filter, chunkSize = 100) {
        let page = 1;
        let hasMore = true;
        while (hasMore) {
            // Note: Backend doesn't support pagination yet
            // This is a placeholder for future pagination support
            const tasks = await this.taskService.getAdminListView(filter);
            if (tasks.length === 0) {
                hasMore = false;
                break;
            }
            // Client-side chunking
            for (let i = 0; i < tasks.length; i += chunkSize) {
                const chunk = tasks.slice(i, i + chunkSize);
                if (chunk.length > 0) {
                    yield chunk;
                }
            }
            // Since we're loading all at once, stop after first iteration
            hasMore = false;
        }
    }
    /**
     * Stream tasks created by current user
     * @param filter - Filter criteria
     * @param chunkSize - Number of tasks per chunk (default: 50)
     * @yields Arrays of task model views as chunks
     */
    async *streamMyCreatedTasks(filter, chunkSize = 50) {
        const tasks = await this.taskService.getMyCreatedTasks(filter);
        if (tasks.length === 0) {
            return;
        }
        // Client-side chunking
        for (let i = 0; i < tasks.length; i += chunkSize) {
            const chunk = tasks.slice(i, i + chunkSize);
            if (chunk.length > 0) {
                yield chunk;
            }
        }
    }
    /**
     * Load all tasks with progress callback for large datasets
     * @param filter - Filter criteria
     * @param chunkSize - Number of tasks per chunk
     * @param onProgress - Progress callback (current, total)
     * @returns All loaded tasks
     *
     * @example
     * ```typescript
     * const tasks = await streamingService.loadAllWithProgress(
     *   filter,
     *   50,
     *   (loaded, total) => console.log(`Progress: ${loaded}/${total}`)
     * );
     * ```
     */
    async loadAllWithProgress(filter, chunkSize = 50, onProgress) {
        const allTasks = [];
        let totalLoaded = 0;
        for await (const chunk of this.streamTasks(filter, chunkSize)) {
            allTasks.push(...chunk);
            totalLoaded += chunk.length;
            if (onProgress) {
                // Estimate total as we go (we'll know exact total after first chunk in current impl)
                onProgress(totalLoaded, totalLoaded);
            }
        }
        return allTasks;
    }
    /**
     * Load admin tasks with progress callback
     * @param filter - Admin filter criteria
     * @param chunkSize - Number of tasks per chunk
     * @param onProgress - Progress callback (current, total)
     * @returns All loaded tasks
     */
    async loadAdminAllWithProgress(filter, chunkSize = 100, onProgress) {
        const allTasks = [];
        let totalLoaded = 0;
        for await (const chunk of this.streamAdminTasks(filter, chunkSize)) {
            allTasks.push(...chunk);
            totalLoaded += chunk.length;
            if (onProgress) {
                onProgress(totalLoaded, totalLoaded);
            }
        }
        return allTasks;
    }
}
//# sourceMappingURL=TaskStreamingService.js.map